import { useState, useEffect } from 'react';
import { 
  FaChevronLeft,
  FaChevronRight,
  FaPills,
  FaUserNurse,
  FaVial,
  FaUserMd,
  FaCalendarCheck,
  FaAmbulance,
  FaVideo
} from 'react-icons/fa';

// Import images
import medicineImg from '../assets/medecine.png';
import paramedicalImg from '../assets/paramedical.png';
import medicalTestImg from '../assets/medicaltest.png';
import consultancyImg from '../assets/consultancy.png';
import bookAppointmentImg from '../assets/bookappointment.png';
import ambulanceImg from '../assets/ambulance.png';

export default function FullScreenHealthcareCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const services = [
    {
      id: 1,
      title: 'Order Medicine',
      description: 'Get your medicines delivered safely to your doorstep with our reliable pharmacy service',
      action: () => window.location.href = '/medicines',
      color: '#2196F3',
      bgGradient: 'linear-gradient(135deg, #1976D2 0%, #2196F3 50%, #42A5F5 100%)',
      icon: FaPills,
      image: medicineImg
    },
    {
      id: 2,
      title: 'Paramedical Service',
      description: 'Professional healthcare services at home with qualified paramedical staff',
      action: () => window.location.href = '/paramedical',
      color: '#4CAF50',
      bgGradient: 'linear-gradient(135deg, #388E3C 0%, #4CAF50 50%, #66BB6A 100%)',
      icon: FaUserNurse,
      image: paramedicalImg
    },
    {
      id: 3,
      title: 'Medical Test',
      description: 'Book comprehensive lab tests and health checkups from the comfort of your home',
      action: () => window.location.href = '/medical-tests',
      color: '#FF9800',
      bgGradient: 'linear-gradient(135deg, #F57C00 0%, #FF9800 50%, #FFB74D 100%)',
      icon: FaVial,
      image: medicalTestImg
    },
    {
      id: 4,
      title: 'Video Consultation',
      description: 'Instant video consultation with certified doctors - available 24/7 from anywhere',
      action: () => window.location.href = '/online-consultation',
      color: '#104a5f',
      bgGradient: 'linear-gradient(135deg, #0d3b4a 0%, #104a5f 50%, #1a5f7a 100%)',
      icon: FaVideo,
      image: consultancyImg
    },
    {
      id: 5,
      title: 'Book Appointment',
      description: 'Schedule appointments with specialists and get the best healthcare services',
      action: () => window.location.href = '/doctor-list',
      color: '#F44336',
      bgGradient: 'linear-gradient(135deg, #D32F2F 0%, #F44336 50%, #EF5350 100%)',
      icon: FaCalendarCheck,
      image: bookAppointmentImg
    },
    {
      id: 6,
      title: 'Book Ambulance',
      description: '24/7 emergency ambulance service available at your fingertips',
      action: () => window.open('tel:+917759847474'),
      color: '#FF5722',
      bgGradient: 'linear-gradient(135deg, #E64A19 0%, #FF5722 50%, #FF7043 100%)',
      icon: FaAmbulance,
      image: ambulanceImg
    }
  ];

  // Auto-slide functionality - only when not hovered
  useEffect(() => {
    if (isHovered) return; // Don't auto-slide when hovered
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % services.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [services.length, isHovered]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % services.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + services.length) % services.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Render service image
  const renderServiceImage = (service) => {
    return (
      <div className="absolute inset-0 w-full h-full flex items-center justify-center">
        <img 
          src={service.image} 
          alt={service.title}
          className="w-full h-full object-cover transition-all duration-300"
          onError={(e) => {
            // Fallback to icon if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        {/* Fallback icon */}
        <div className="hidden w-full h-full items-center justify-center bg-black bg-opacity-20 backdrop-blur-sm rounded-3xl border-2 border-white border-opacity-30">
          <service.icon className="text-6xl sm:text-8xl text-white opacity-80" />
        </div>
      </div>
    );
  };

  return (
    <div 
      className="relative w-full h-[50vh] sm:h-[60vh] lg:h-[80vh] xl:h-[90vh] overflow-hidden carousel-container"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main Carousel */}
      <div className="relative w-full h-full flex">
        {services.map((service, index) => {
          const isActive = index === currentSlide;
          
          return (
            <div
              key={service.id}
              className="absolute w-full h-full transition-all duration-800 ease-in-out cursor-pointer overflow-hidden"
              style={{
                background: service.bgGradient,
                transform: `translateX(${(index - currentSlide) * 100}%)`,
                opacity: isActive ? 1 : 0,
                zIndex: isActive ? 2 : 1
              }}
              onClick={(e) => {
                // Only trigger slide action if not clicking the CTA button
                if (!e.target.closest('.cta-button-hero')) {
                  service.action();
                }
              }}
            >
              <div className="relative w-full h-full">
                {/* Background Image - Full Screen */}
                <div className="absolute inset-0 w-full h-full">
                  {renderServiceImage(service)}
                </div>
                
                {/* Light Overlay for better text readability */}
                <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                {/* Light gradient overlay for bottom text */}
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black from-5% via-black via-15% to-transparent opacity-40"></div>
                
                {/* Text Content - Bottom Center Single Line */}
                <div className="absolute bottom-6 sm:bottom-8 lg:bottom-12 left-0 right-0 text-white z-10">
                  <div className="text-center">
                    <div className="bottom-content flex flex-row items-center justify-center gap-3 sm:gap-4 flex-wrap">
                      <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white text-shadow-lg">
                        {service.title}
                      </h1>
                      
                      <div 
                        className="cta-button-hero inline-flex items-center gap-2 bg-white bg-opacity-20 border-2 border-white border-opacity-60 rounded-full px-4 sm:px-6 py-2 sm:py-3 backdrop-blur-sm transition-all duration-300 hover:bg-opacity-30 hover:scale-105 shadow-lg cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (service.title === 'Book Ambulance') {
                            window.open('tel:+917759847474');
                          } else {
                            service.action();
                          }
                        }}
                      >
                        <span className="text-white text-sm sm:text-base font-semibold">
                          {service.title === 'Book Ambulance' ? '📞 Call Now' : 'Get Started'}
                        </span>
                        <div className="text-base transition-transform duration-300">→</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Background decorative elements - Removed */}
            </div>
          );
        })}
      </div>

      {/* Navigation Controls */}
      <button 
        className="absolute top-1/2 left-2 sm:left-4 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 border-none rounded-full w-10 sm:w-12 lg:w-14 h-10 sm:h-12 lg:h-14 flex items-center justify-center cursor-pointer text-gray-800 z-10 transition-all duration-300 shadow-lg hover:scale-110"
        onClick={prevSlide}
      >
        <FaChevronLeft className="text-sm sm:text-base lg:text-lg" />
      </button>
      
      <button 
        className="absolute top-1/2 right-2 sm:right-4 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 border-none rounded-full w-10 sm:w-12 lg:w-14 h-10 sm:h-12 lg:h-14 flex items-center justify-center cursor-pointer text-gray-800 z-10 transition-all duration-300 shadow-lg hover:scale-110"
        onClick={nextSlide}
      >
        <FaChevronRight className="text-sm sm:text-base lg:text-lg" />
      </button>

      {/* Dots Indicator - Removed as requested */}

      {/* Service Counter */}
      <div className="absolute top-4 sm:top-6 lg:top-8 right-4 sm:right-6 lg:right-8 z-10">
        <span className="text-white text-xs sm:text-sm font-semibold bg-black bg-opacity-20 backdrop-blur-md px-2 sm:px-3 py-1 sm:py-2 rounded-full">
          {String(currentSlide + 1).padStart(2, '0')} / {String(services.length).padStart(2, '0')}
        </span>
      </div>

      {/* Custom Styles */}
      <style>{`
        .text-shadow {
          text-shadow: 2px 2px 8px rgba(0,0,0,0.7);
        }

        .text-shadow-lg {
          text-shadow: 3px 3px 12px rgba(0,0,0,0.8);
        }

        /* Ensure smooth transitions */
        .carousel-container * {
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Single line bottom layout styling - Always horizontal */
        .bottom-content {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          justify-content: center !important;
          flex-wrap: nowrap !important;
        }
        
        @media (max-width: 640px) {
          .cta-button-hero {
            min-width: 44px;
            min-height: 44px;
            padding: 6px 12px !important;
            white-space: nowrap;
          }
          
          .cta-button-hero span {
            font-size: 11px !important;
          }
          
          .bottom-content {
            gap: 8px !important;
          }
          
          h1 {
            font-size: 16px !important;
            white-space: nowrap;
          }
        }
        
        @media (max-width: 480px) {
          .cta-button-hero {
            padding: 4px 8px !important;
          }
          
          .cta-button-hero span {
            font-size: 10px !important;
          }
          
          .bottom-content {
            gap: 6px !important;
          }
          
          h1 {
            font-size: 14px !important;
          }
        }
        
        @media (max-width: 320px) {
          h1 {
            font-size: 12px !important;
          }
          
          .cta-button-hero span {
            font-size: 9px !important;
          }
          
          .cta-button-hero {
            padding: 3px 6px !important;
          }
        }
      `}</style>
    </div>
  );
}